// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../components/forms/ActionForms/constants';
import { externalCompaniesNaming } from '../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';
import { formAccidentNaming } from '../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../components/forms/AddVehicleForm/constants';
import { formNaming as formCommunicationNaming } from '../components/forms/NewCommunicationForm/constants';
import { formOrderNaming } from '../components/forms/NewWorkOrderForm/constants';

// Utils
import { defineDraftScope } from './draftOwnership';
import {
  NEW_ACTION_EXCLUDED_VEHICLES_KEY,
  VALIDATION_EXCLUDED_VEHICLES_KEY,
} from './excludedVehiclesSync';
import { decompressSessionStorage } from './sessionStorage';

/**
 * Ámbitos de borradores de los asistentes de actuación (spec
 * `2026-11-02-wizard-draft-ownership-design.md` § 6). Cada uno dice qué claves
 * de `sessionStorage` son borrador de UNA actuación, para que
 * `claimDraftScope` las descarte cuando se abre otra.
 */

/**
 * Copias de las pestañas del asistente de VALIDACIÓN, con dueño =
 * `:id` de la actuación de la URL.
 *
 * 🔴 No incluye el puntero de contexto `new-validation-action-data`: lo
 * escriben las puertas que limpian (ojo de la tarjeta, tabla de Validación…) y
 * su regla es otra — lo gobierna `reconcileValidationActionContext`.
 */
export const ACTION_VALIDATION_DRAFT_KEYS: readonly string[] = [
  ...Object.values(formActionValidationNaming),
  // Cachés con sufijo que cuelgan de las claves base.
  `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_interno`,
  `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_externals`,
  `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_drivers`,
  `${formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM}-vehicle`,
  `${formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM}-machinery`,
  // Cambios de empresas externas pendientes de guardar.
  ...Object.values(externalCompaniesNaming),
  // Vehículos quitados en Maquinaria: su lista se ata al id del contexto
  // principal, y sin contexto A y B daban los dos `null` → B heredaba lo que
  // se quitó en A (revisión del 2-nov).
  VALIDATION_EXCLUDED_VEHICLES_KEY,
];

const validationDraftKeys = new Set(ACTION_VALIDATION_DRAFT_KEYS);

export const ACTION_VALIDATION_SCOPE = defineDraftScope({
  name: 'action-validation',
  // La clave del primer arreglo (8507ee85): mismo nombre para que las sesiones
  // abiertas no pierdan su dueño al desplegar. Fuera de
  // `formActionValidationNaming` a propósito: sus valores se recorren dando por
  // hecho que cada uno es un formulario comprimido.
  ownerKey: 'new-validation-cache-owner',
  isDraftKey: (key) => validationDraftKeys.has(key),
});

/**
 * Fotos y croquis de una actuación: la LISTA de fotos y el croquis en curso.
 * Los comparten crear y validar (mismas claves), así que el ámbito es UNO:
 * cambiar de actuación en cualquiera de los dos asistentes descarta la lista
 * de la anterior.
 *
 * 🔴 Las carpetas de OPFS (`action`, `canvas-action`) NO se vacían (Ruling 5):
 * son de todo el origen y las comparten las pestañas — vaciarlas tiraría las
 * fotos sin guardar de OTRA pestaña. Un fichero de otra actuación que se quede
 * en la carpeta no se ve ni se sube: en validación, «Datos Act» pinta las
 * fotos de `editionImages` (copia `new-validation-edition-form`) y el guardado
 * sube solo las altas de `data-changes` (`onlyNames`) — las dos del ámbito de
 * validación, que se descartan al cambiar de actuación. Crear se alinea en la
 * Task 5.
 *
 * `selected-actions-files` hoy solo se LEE (`ActionEdition`, ficheros en
 * memoria); nadie la escribe. Va en el ámbito para que, si una puerta vuelve a
 * escribirla, no cruce de una actuación a otra.
 */
const actionMediaKeys = new Set<string>([
  formActionNaming.SELECTED_ACTIONS_FILES,
  formActionNaming.IMAGE_CANVAS,
  formActionNaming.SVG_CANVAS,
  formActionNaming.ARRAY_CANVAS,
]);

export const ACTION_MEDIA_SCOPE = defineDraftScope({
  name: 'action-media',
  ownerKey: 'new-action-media-owner',
  isDraftKey: (key) => actionMediaKeys.has(key),
});

/**
 * Copias de las pestañas del asistente de CREACIÓN de una actuación, con
 * dueño = `draftId` del borrador en curso (spec 2026-11-02 § 6, «Crear
 * actuación sin `actionId` todavía»).
 *
 * Crear no lleva id en la URL (`new-action/details/…`) y, sin red o en el paso
 * 1, ni siquiera hay `actionId`. Cada puerta que EMPIEZA una creación (el
 * `pass-data` de «Crear actuación», `createBasicAction` del cronómetro y la
 * pastilla del tramo, y las semillas sin red) escribe un `draftId` nuevo en
 * `new-action-data`; los loaders del asistente reclaman este ámbito con él.
 *
 * Fuera, a propósito:
 * - `new-action-data`: es el PUNTERO del borrador (lleva el `draftId`). Lo
 *   reescriben las puertas; descartarlo al reclamar dejaría sin borrador al
 *   asistente que lo está abriendo.
 * - `new-validation-action-data`: el contexto de VALIDACIÓN, que tiene su
 *   propia regla (`reconcileValidationActionContext`).
 *
 * La lista de fotos y el croquis (`selected-actions-files`, `action-*-canvas`)
 * entran también aquí porque son claves de `formActionNaming`; su dueño de
 * verdad es `ACTION_MEDIA_SCOPE`, que los loaders de creación reclaman con el
 * MISMO `draftId`. Estar en los dos ámbitos no cambia nada: se descartan cuando
 * cambia el borrador, lo detecte el uno o el otro.
 */
const NOT_CREATION_DRAFT_KEYS = new Set<string>([
  formActionNaming.NEW_ACTION_DATA,
  formActionNaming.NEW_VALIDATION_ACTION_DATA,
]);

export const ACTION_CREATION_DRAFT_KEYS: readonly string[] = [
  ...Object.values(formActionNaming).filter((key) => !NOT_CREATION_DRAFT_KEYS.has(key)),
  // Cachés con sufijo que cuelgan de las claves base (las mismas que borra
  // `removeNewActionStorage`).
  `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_interno`,
  `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_externals`,
  `${formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM}_drivers`,
  `${formActionNaming.NEW_WORK_EQUIPMENT_FORM}-vehicle`,
  `${formActionNaming.NEW_WORK_EQUIPMENT_FORM}-machinery`,
  // Vehículos quitados en Maquinaria: atados al `actionId`, que sin red o en
  // el paso 1 aún no existe — dos borradores sin id compartían la lista.
  NEW_ACTION_EXCLUDED_VEHICLES_KEY,
];

const creationDraftKeys = new Set(ACTION_CREATION_DRAFT_KEYS);

export const ACTION_CREATION_SCOPE = defineDraftScope({
  name: 'action-creation',
  ownerKey: 'new-action-draft-owner',
  isDraftKey: (key) => creationDraftKeys.has(key),
});

/**
 * Última lectura de `new-action-data`. La comparten `<EntityScreen>` (en cada
 * render de la pantalla del asistente) y los loaders (dos en paralelo por
 * navegación): el borrador lleva los catálogos de la fase 1 y descomprimirlo
 * cada vez es trabajo tirado. Se rehace solo si cambia la cadena guardada.
 */
let lastCreationDraft: { raw: string; draft: Record<string, unknown> | null } | null = null;

/** Congela en profundidad (una vez, al cachear): nadie puede tocar la caché compartida. */
const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value as Record<string, unknown>).forEach(deepFreeze);
  }
  return value;
};

/** Lectura con caché de `new-action-data`: el objeto COMPARTIDO y congelado. Nunca lanza. */
const readCachedCreationDraft = (
  store: Pick<Storage, 'getItem'>
): Record<string, unknown> | null => {
  let raw: string | null;
  try {
    raw = store.getItem(formActionNaming.NEW_ACTION_DATA);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (lastCreationDraft?.raw === raw) return lastCreationDraft.draft;

  let draft: Record<string, unknown> | null = null;
  try {
    const parsed = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    draft =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? deepFreeze(parsed) : null;
  } catch {
    draft = null;
  }
  lastCreationDraft = { raw, draft };
  return draft;
};

/**
 * El borrador de creación en curso (`new-action-data`), o `null`. Nunca lanza.
 *
 * Devuelve el objeto de la caché CONGELADO (Ruling 19/20): lo comparten todas
 * las lecturas, y un llamante que lo tocara cambiaría lo que leen los demás
 * (la `key` de `<EntityScreen>`, los loaders en paralelo) sin que cambie la
 * cadena guardada. Ningún llamante lo modifica; congelado, el que lo intente
 * falla en el acto en vez de corromperlo. Se congela una vez al cachear, no
 * se copia en cada lectura (el resumen lo lee en cada render).
 */
export const readCreationDraft = (
  store: Pick<Storage, 'getItem'> = sessionStorage
): Readonly<Record<string, unknown>> | null => readCachedCreationDraft(store);

/**
 * Id local del borrador de creación en curso (`draftId` de `new-action-data`),
 * o `null` si no hay borrador o es de antes de este cambio (sesiones abiertas
 * al desplegar, APK restaurada, semillas a mano de los E2E). Sin `draftId` el
 * reclamo no hace nada y la pantalla no remonta: el comportamiento de antes
 * (Ruling 8), hasta que la siguiente puerta escriba uno.
 *
 * Es la `key` de `<EntityScreen>` en las rutas `details` de los tres árboles
 * `new-action`: cuando el cronómetro crea la básica B estando en Ubicación de
 * la A y redirige a la MISMA URL, cambia el `draftId` y la pantalla se monta
 * de cero (auditoría de creación 2.1, M2).
 */
export const readCreationDraftId = (
  store: Pick<Storage, 'getItem'> = sessionStorage
): string | null => {
  // Solo lee un campo: la lectura compartida, sin copiar los catálogos.
  const draftId = readCachedCreationDraft(store)?.draftId;
  return typeof draftId === 'string' && draftId.length > 0 ? draftId : null;
};

/**
 * Copias de los tres pasos de EDITAR ACCIDENTE, con dueño = `:id` de la URL
 * (la Order del accidente: es el id con el que navegan el lápiz de la tarjeta
 * y el «Editar» de la tabla).
 *
 * R5 del spec (2026-11-02): con el lápiz de B y atrás hasta la URL de editar
 * A, la pantalla enseñaba B entero y «Guardar» escribía en B — los loaders
 * montaban `{...servidor, ...sesión}` y los pasos 2 y 3 sacaban la Order de
 * la copia, no de la URL (auditoría de accidentes § 0-§ 4).
 *
 * Las fotos de la edición viajan DENTRO de la copia del paso 2
 * (`edit-basic-info-accident-form`, campo `images`): esa es la lista que pinta
 * `AccidentAttention` y de la que el guardado saca qué subir (`imagesToSave`).
 * Al ser clave de este ámbito, se descarta con él.
 */
export const ACCIDENT_EDIT_DRAFT_KEYS: readonly string[] = [
  formAccidentNaming.EDIT_ACCIDENT_FORM,
  formAccidentNaming.EDIT_BASIC_INFO_ACCIDENT_FORM,
  formVehicleNaming.EDIT_VEHICLE_FORM,
];

const accidentEditDraftKeys = new Set(ACCIDENT_EDIT_DRAFT_KEYS);

export const ACCIDENT_EDIT_SCOPE = defineDraftScope({
  name: 'accident-edit',
  ownerKey: 'edit-accident-owner',
  isDraftKey: (key) => accidentEditDraftKeys.has(key),
});

/**
 * Copias de los tres pasos del ALTA de un accidente, con dueño =
 * `parent:<:id>` — el `:id` de la URL es la OT (o la campaña) PADRE, porque el
 * accidente todavía no existe (ver `accidentCreationOwnerId`).
 *
 * S4 de la auditoría de accidentes (2026-11-02): con el alta de la OT P1 a
 * medias, abrir a mano `/work-orders/P2/new-accident` enseñaba el borrador de
 * P1 y el accidente se creaba BAJO P1 (el padre salía de
 * `new-accident-form.orderParentId`, no de la URL).
 *
 * Las fotos del alta viajan DENTRO de la copia del paso 2
 * (`add-basic-info-accident-form`, campo `images`) y el croquis en curso
 * también (campo `sketch`, con su `fileName`): esa copia es la LISTA que pinta
 * `AccidentAttention` y la que el guardado sube, así que se descartan con este
 * ámbito. Las claves de sesión del croquis (`image-canvas`…) son de
 * `ACCIDENT_MEDIA_SCOPE`, que los loaders del alta reclaman con el MISMO id.
 *
 * Fuera, a propósito: las copias de EDITAR (`ACCIDENT_EDIT_SCOPE`) y el
 * contexto de navegación (lo usan el detalle y la edición, con su regla).
 */
export const ACCIDENT_CREATION_DRAFT_KEYS: readonly string[] = [
  formAccidentNaming.NEW_ACCIDENT_FORM,
  formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM,
  formVehicleNaming.NEW_VEHICLE_FORM,
];

const accidentCreationDraftKeys = new Set(ACCIDENT_CREATION_DRAFT_KEYS);

export const ACCIDENT_CREATION_SCOPE = defineDraftScope({
  name: 'accident-creation',
  ownerKey: 'new-accident-owner',
  isDraftKey: (key) => accidentCreationDraftKeys.has(key),
});

/**
 * Dueño del alta de un accidente bajo el padre `parentId` (`:id` de la URL
 * `…/:id/new-accident`): `parent:<id>`. El prefijo separa este espacio de ids
 * del de editar (la Order del accidente) en el ámbito de croquis, que es
 * compartido: pasar de editar a crear (o al revés) descarta lo del otro.
 * Sin padre, `null` (el reclamo no hace nada).
 */
export const accidentCreationOwnerId = (
  parentId: string | null | undefined
): string | null => (parentId ? `parent:${parentId}` : null);

/**
 * Croquis en curso de un accidente (y la lista `selected-accidents-files`).
 * Las comparten crear y editar accidente (mismas claves), así que el ámbito
 * es UNO: editar lo reclama con la Order del accidente y crear con
 * `parent:<id>` de la OT padre (`accidentCreationOwnerId`) — espacios de id
 * distintos, cambiar de uno a otro descarta lo del anterior.
 *
 * 🔴 Solo claves de `sessionStorage`. Las carpetas de OPFS (`accident`,
 * `canvas-accident`) NO se vacían (Ruling 5): son de todo el origen y las
 * comparten las pestañas. Lo que no está en la lista con dueño ni se pinta ni
 * se sube (Ruling 6: la lista real de fotos es el campo `images` de la copia
 * del paso 2, que es de `ACCIDENT_EDIT_SCOPE` al editar y de
 * `ACCIDENT_CREATION_SCOPE` al crear; el croquis, el campo `sketch` de esa
 * misma copia, que dice con qué NOMBRE de fichero se guardó).
 *
 * `selected-accidents-files` no tiene escritor en `src` (igual que
 * `selected-actions-files`); va aquí para que, si una puerta vuelve a
 * escribirla, no cruce de un accidente a otro.
 */
export const ACCIDENT_MEDIA_DRAFT_KEYS: readonly string[] = [
  formAccidentNaming.SELECTED_ACCIDENTS_FILES,
  formAccidentNaming.IMAGE_CANVAS,
  formAccidentNaming.SVG_CANVAS,
  formAccidentNaming.ARRAY_CANVAS,
];

const accidentMediaDraftKeys = new Set(ACCIDENT_MEDIA_DRAFT_KEYS);

export const ACCIDENT_MEDIA_SCOPE = defineDraftScope({
  name: 'accident-media',
  ownerKey: 'new-accident-media-owner',
  isDraftKey: (key) => accidentMediaDraftKeys.has(key),
});

/**
 * Copia de EDITAR COMUNICACIÓN, con dueño = `:id` de la URL (la Order gemela de
 * la comunicación: es el id con el que navegan el lápiz de la tarjeta y el
 * «Editar» de la tabla).
 *
 * S2/S3 de la auditoría de accidentes y comunicaciones (2026-11-02): el loader
 * montaba `{...servidor, ...copia}` sin mirar de quién era la copia y el
 * guardado escribía en `loaderData.id` (la copia gana), así que con atrás o con
 * una URL a mano la pantalla de A enseñaba y guardaba B.
 *
 * Las claves con sufijo (`-OT`, `-Campaign`) son claves de `modal` de Redux
 * (`selectedRowModalTable`), nunca de `sessionStorage`; las puertas y el
 * guardado las `removeItem`-an por si acaso. Entran en el ámbito porque no
 * cuesta nada, pero la copia real es `edit-communication-form`.
 *
 * 🔴 NO incluye `communication-navigation-context`: lo comparten el detalle y
 * crear comunicación; su regla es el `entityId` (`readOwnCommunicationNavigationContext`).
 */
export const COMMUNICATION_EDIT_DRAFT_KEYS: readonly string[] = [
  formCommunicationNaming.EDIT_COMMUNICATION_FORM,
  formCommunicationNaming.EDIT_COMMUNICATION_FORM_OT,
  formCommunicationNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN,
];

const communicationEditDraftKeys = new Set(COMMUNICATION_EDIT_DRAFT_KEYS);

export const COMMUNICATION_EDIT_SCOPE = defineDraftScope({
  name: 'communication-edit',
  ownerKey: 'edit-communication-owner',
  isDraftKey: (key) => communicationEditDraftKeys.has(key),
});

/**
 * Copia de EDITAR CAMPAÑA, con dueño = `:id` de la URL (la Order de la
 * campaña: es el id con el que navega el «Editar» de la lista).
 *
 * El loader montaba `{...servidor, ...copia}` sin mirar de quién era la copia
 * y su única puerta (`Campaigns/index.tsx`) la borraba, así que con atrás o
 * con una URL a mano la pantalla de A enseñaba y guardaba B (auditoría de
 * accidentes y comunicaciones § 5).
 */
export const CAMPAIGN_EDIT_DRAFT_KEYS: readonly string[] = [
  formOrderNaming.EDIT_CAMPAIGN_FORM,
];

const campaignEditDraftKeys = new Set(CAMPAIGN_EDIT_DRAFT_KEYS);

export const CAMPAIGN_EDIT_SCOPE = defineDraftScope({
  name: 'campaign-edit',
  ownerKey: 'edit-campaign-owner',
  isDraftKey: (key) => campaignEditDraftKeys.has(key),
});

/**
 * Copia de DUPLICAR OT, con dueño = `:id` de `new-order-duplicate/:id`, es
 * decir, la OT ORIGEN: el duplicado aún no existe. Con el duplicado de A a
 * medias, abrir a mano el de B enseñaba la copia de A encima de los datos de B.
 */
export const ORDER_DUPLICATE_DRAFT_KEYS: readonly string[] = [
  formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM,
];

const orderDuplicateDraftKeys = new Set(ORDER_DUPLICATE_DRAFT_KEYS);

export const ORDER_DUPLICATE_SCOPE = defineDraftScope({
  name: 'order-duplicate',
  ownerKey: 'new-duplicate-order-owner',
  isDraftKey: (key) => orderDuplicateDraftKeys.has(key),
});

/**
 * Copia de DUPLICAR COMUNICACIÓN, con dueño = `:id` de
 * `new-communication-duplicate/:id` (la comunicación ORIGEN). Las claves con
 * sufijo (`-OT`, `-Campaign`) son claves de `modal` de Redux; entran por
 * coherencia con `COMMUNICATION_EDIT_DRAFT_KEYS`.
 */
export const COMMUNICATION_DUPLICATE_DRAFT_KEYS: readonly string[] = [
  formCommunicationNaming.NEW_DUPLICATE_COMMUNICATION_FORM,
  formCommunicationNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT,
  formCommunicationNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN,
];

const communicationDuplicateDraftKeys = new Set(COMMUNICATION_DUPLICATE_DRAFT_KEYS);

export const COMMUNICATION_DUPLICATE_SCOPE = defineDraftScope({
  name: 'communication-duplicate',
  ownerKey: 'new-duplicate-communication-owner',
  isDraftKey: (key) => communicationDuplicateDraftKeys.has(key),
});

/**
 * Copia del ALTA de una comunicación («Nueva comunicación»), con dueño =
 * `parent:<:id>` en los árboles de OT y de campaña (el `:id` de la URL
 * `…/:id/new-communication` es el PADRE: la comunicación aún no existe) y
 * `standalone` en la sección suelta (`/communication`, sin padre en la URL).
 * Ver `communicationCreationOwnerId`.
 *
 * K3 de la batería QA del navegador (2026-11-03): con la comunicación de la OT
 * 801 a medias, abrir `/work-orders/800/new-communication` a mano (o con
 * atrás) enseñaba la miga de 801 y `saveData` creaba la comunicación BAJO 801:
 * el loader solo distinguía «árbol de OT ↔ sección suelta», no «OT A ↔ OT B».
 *
 * Las claves con sufijo (`-OT`, `-Campaign`) son claves de `modal` de Redux
 * (`selectedRowModalTable`, persistidas en `localStorage`), nunca de
 * `sessionStorage`: entran por coherencia con editar/duplicar. Su descarte de
 * verdad lo hace el loader al cambiar de dueño
 * (`releaseCommunicationCreationSelection`).
 *
 * 🔴 NO incluye `communication-navigation-context`: lo comparten el detalle,
 * editar y crear comunicación; cada uno con su regla.
 */
/**
 * OT/campaña elegidas A MANO en el modal de relación para ESTA alta
 * (`{ [clave del modal]: id }`). La selección del modal vive en Redux, que
 * redux-persist guarda en `localStorage` — compartido entre pestañas —, así que
 * al recargar una pestaña puede volver la elegida en OTRA. Esta lista es de la
 * pestaña (`sessionStorage`) y dice cuál es de verdad de esta alta (revisión
 * de la Task 12a, residuo entre pestañas del Ruling 11).
 */
export const COMMUNICATION_CREATION_PICKS_KEY = 'new-communication-relation-picks';

export const COMMUNICATION_CREATION_DRAFT_KEYS: readonly string[] = [
  formCommunicationNaming.NEW_COMMUNICATION_FORM,
  formCommunicationNaming.NEW_COMMUNICATION_FORM_OT,
  formCommunicationNaming.NEW_COMMUNICATION_FORM_CAMPAIGN,
  COMMUNICATION_CREATION_PICKS_KEY,
];

const communicationCreationDraftKeys = new Set(COMMUNICATION_CREATION_DRAFT_KEYS);

export const COMMUNICATION_CREATION_SCOPE = defineDraftScope({
  name: 'communication-creation',
  ownerKey: 'new-communication-owner',
  isDraftKey: (key) => communicationCreationDraftKeys.has(key),
});

/** Dueño del alta en la sección suelta (`/communication`): no hay padre en la URL. */
export const COMMUNICATION_CREATION_STANDALONE_OWNER = 'standalone';

/**
 * Dueño del alta de una comunicación: `parent:<id>` bajo la OT o la campaña
 * `parentId` (`:id` de `…/:id/new-communication`) y `standalone` sin padre en
 * la URL. El prefijo es el mismo que el del alta de accidente
 * (`accidentCreationOwnerId`): un id de OT nunca se confunde con el dueño de
 * la sección suelta.
 */
export const communicationCreationOwnerId = (parentId: string | null | undefined): string =>
  parentId ? `parent:${parentId}` : COMMUNICATION_CREATION_STANDALONE_OWNER;
